import { useCallback } from 'react';
import { useRouter } from 'next/router';

import { DownloadFileData } from '@keaze/web/components/downloadFile';
import {
  Brochure,
  PropertyDetails,
  sitePlanImages,
} from '@keaze/web/common/interfaces';
import { OnPlanClick } from './property.types';
import { AvailabilityStatusCode, QueryModals } from '@keaze/web/common/enums';
import { onQueryDialogClick } from '@keaze/web/utils/queryDialog';
import {
  convertBrochuresToDownloadFiles,
  convertSiteplanToDownloadFiles,
} from '../helpers/converts';

type UsePropertyExpected = {
  sitePlanImagesDownloadFiles: DownloadFileData[];
  brochuresDownloadFiles: DownloadFileData[];
  isVisibleFilters: boolean;
  propertiesForListing: PropertyDetails[];
  onPlanClick: OnPlanClick;
};

type UsePropertyArgs = {
  properties: PropertyDetails[];
  isListingSold: boolean;
  brochures: Brochure[] | null;
  sitePlanImages: sitePlanImages[] | null;
};

type UseProperty = (args: UsePropertyArgs) => UsePropertyExpected;

type GeneratePropertiesForListing = (
  properties: PropertyDetails[],
  isListingSold: boolean
) => PropertyDetails[];

type IsOnlyOnePropertyAndSold = (properties: PropertyDetails[]) => boolean;

const generatePropertiesForListing: GeneratePropertiesForListing = (
  properties,
  isListingSold
) => {
  if (isListingSold) {
    return properties;
  }

  return properties.filter(
    ({ availabilityStatus: { code } }) => code !== AvailabilityStatusCode.Sold
  );
};

const isOnlyOnePropertyAndSold: IsOnlyOnePropertyAndSold = (properties) => {
  const isOnlyOneProperty = properties.length === 1;

  if (isOnlyOneProperty) {
    return (
      properties[0].availabilityStatus.code === AvailabilityStatusCode.Sold
    );
  }

  return isOnlyOneProperty;
};

export const useProperty: UseProperty = ({
  properties,
  isListingSold,
  brochures,
  sitePlanImages,
}) => {
  const router = useRouter();

  const propertiesForListing = generatePropertiesForListing(
    properties,
    isListingSold
  );

  const isVisibleFilters =
    propertiesForListing.length !== 0 && !isOnlyOnePropertyAndSold(properties);

  const handlePlanClick: OnPlanClick = useCallback(
    (id) => onQueryDialogClick({ router, name: QueryModals.plan, value: id }),
    [router.query]
  );

  return {
    sitePlanImagesDownloadFiles: convertSiteplanToDownloadFiles(sitePlanImages),
    brochuresDownloadFiles: convertBrochuresToDownloadFiles(brochures),
    isVisibleFilters,
    propertiesForListing,
    onPlanClick: handlePlanClick,
  };
};
